// Collection page: search + faceted filters (Vendor / Product Type /
// Birthstone / Colour / Price) + sort + "Load More", all reacting together
// against one shared grid — ported from the approved prototype's shop.js.
//
// Real-store change: PRODUCTS_DATA is no longer a static generated file.
// The first 250 products are already embedded in the page as JSON (see
// main-collection.liquid's `[data-product-feed]` script tag, hand-built
// from real Liquid product/variant data). If the collection has more than
// 250 products, loadAllProducts() below fetches the rest through
// Shopify's documented Section Rendering API (one request per extra 250,
// `?section_id=...&page=N`) and pulls the same embedded JSON back out of
// each response — so this page always reflects whatever is actually
// published on the store, with no rebuild step.
//
// Birthstone / Colour / Best Seller facets read Shopify PRODUCT TAGS,
// using the convention: `birthstone_<month>` (e.g. birthstone_august),
// `color_<value>` (e.g. color_green), and `bestseller`. Ring sizing that
// needs the "select a size before you can buy" gate reads the tag
// `ring-adjustable` (adjustable rings skip the gate) together with the
// product's real Shopify "Size" option. These tags must exist on the real
// products for the corresponding facet/behaviour to work — see THEME
// README.md for how to apply them from the same product export CSV the
// prototype's own data already came from.
(function () {
  const grid = document.querySelector('[data-product-grid]');
  if (!grid) return;

  const sortSelects = document.querySelectorAll('[data-sort], [data-sort-mobile]');
  const emptyState = document.querySelector('[data-empty-state]');
  const loadMoreBtn = document.querySelector('[data-load-more]');
  const loadMoreWrap = document.querySelector('[data-load-more-wrap]');
  const shownCount = document.querySelector('[data-shown-count]');
  const searchInput = document.querySelector('[data-search-input]');
  const clearButtons = document.querySelectorAll('[data-clear-filters]');
  const loadingState = document.querySelector('[data-loading-state]');

  const hasGsap = typeof window.gsap !== 'undefined';
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // This page (/collections/all) is literally every product in the store,
  // which includes a handful of non-jewellery items (e.g. a backpack) sitting
  // in the same catalogue. Rather than hide them, the default "Featured"
  // sort keeps real jewellery first and pushes anything else to the end —
  // recognized by product type (lowercased, so "Ring"/"ring" both match).
  const JEWELRY_TYPES = new Set([
    'ring', 'necklace', 'earring', 'earrings', 'bracelet', 'bracelets',
    'jewellery set', 'jewelry set', 'brooch/scarfpin', 'brooch', 'watch', 'watches',
  ]);
  function isJewelry(p) {
    return JEWELRY_TYPES.has((p.category || '').toLowerCase().trim());
  }

  const MONTH_ORDER = ['january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'];
  const MONTH_LABEL = Object.fromEntries(MONTH_ORDER.map((m) => [m, m[0].toUpperCase() + m.slice(1)]));
  const COLOR_ORDER = ['red', 'pink', 'purple', 'blue', 'green', 'yellow', 'white', 'black', 'multicolor'];
  const COLOR_LABEL = {
    red: 'Red', pink: 'Pink', purple: 'Purple', blue: 'Blue', green: 'Green',
    yellow: 'Yellow', white: 'White / Clear', black: 'Black', multicolor: 'Multicolour',
  };
  const COLOR_SWATCH = {
    red: '#9B111E', pink: '#D68CA0', purple: '#7C5A9E', blue: '#0F52BA', green: '#3F7A5C',
    yellow: '#C98A2B', white: '#EDE7DC', black: '#1C1917',
    multicolor: 'linear-gradient(135deg, #F0D6E8, #C9D6EC, #A98FCB)',
  };

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  function titleCase(str) {
    return String(str).replace(/[-_]+/g, ' ').replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
  }

  function fmtPrice(n) { return window.formatMoney ? window.formatMoney(n) : `R${Math.round(n)}`; }

  /* ---------------- Real catalogue → same shape the UI below expects ---------------- */

  function firstMatch(tags, prefix) {
    const tag = tags.find((t) => t.toLowerCase().startsWith(prefix));
    return tag ? tag.toLowerCase().slice(prefix.length) : null;
  }

  // Real store note: this reads the hand-built feed from
  // main-collection.liquid, NOT Shopify's REST products.json — that
  // endpoint returns variant prices as decimal strings ("650.00"), while
  // Liquid's own variant.price is in cents. The Liquid-side template
  // divides by 100 before this ever runs, so `price`/`compareAtPrice`
  // here are already plain decimal numbers.
  function normalizeProduct(raw) {
    const tags = (raw.tags || []).map((t) => String(t).trim());
    const lowerTags = tags.map((t) => t.toLowerCase());
    const variants = raw.variants || [];
    const cheapest = variants.reduce((a, b) => (b.price < a.price ? b : a), variants[0] || {});
    const price = cheapest.price || 0;
    const compareAt = cheapest.compareAtPrice || null;
    const onSale = !!(compareAt && compareAt > price);

    return {
      handle: raw.handle,
      title: raw.title,
      url: raw.url,
      vendor: raw.vendor,
      category: raw.type || 'other',
      sku: cheapest.sku || '',
      weightGrams: cheapest.weight || 0,
      summary: raw.summary || '',
      price,
      priceDisplay: fmtPrice(price),
      compareAt,
      compareAtDisplay: compareAt ? fmtPrice(compareAt) : null,
      onSale,
      birthstone: firstMatch(lowerTags, 'birthstone_'),
      color: firstMatch(lowerTags, 'color_'),
      bestseller: lowerTags.includes('bestseller'),
      ringAdjustable: lowerTags.includes('ring-adjustable'),
      image: raw.image || '',
      image2: raw.image2 || null,
      firstAvailableVariantId: (variants.find((v) => v.available) || variants[0] || {}).id || null,
    };
  }

  function readFeedFrom(doc) {
    const el = doc.querySelector('[data-product-feed]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }
  }

  function loadAllProducts() {
    const collectionUrl = grid.dataset.collectionUrl;
    const sectionId = grid.dataset.sectionId;
    const totalPages = parseInt(grid.dataset.totalPages, 10) || 1;

    const firstPage = readFeedFrom(document);
    if (totalPages <= 1 || !sectionId) return Promise.resolve(firstPage);

    const extraPages = [];
    for (let page = 2; page <= totalPages; page += 1) extraPages.push(page);

    return Promise.all(extraPages.map((page) =>
      fetch(`${collectionUrl}?section_id=${encodeURIComponent(sectionId)}&page=${page}`)
        .then((res) => (res.ok ? res.text() : ''))
        .then((html) => readFeedFrom(new DOMParser().parseFromString(html, 'text/html')))
        .catch(() => [])
    )).then((rest) => firstPage.concat(...rest));
  }

  /* ---------------- Facet config (category is dynamic — real product_type vocab) ---------------- */

  const FACETS = {
    vendor: {
      get: (p) => p.vendor,
      values: (data) => [...new Set(data.map((p) => p.vendor))].filter(Boolean).sort(),
    },
    category: {
      get: (p) => p.category,
      values: (data) => [...new Set(data.map((p) => p.category))].filter(Boolean).sort(),
      label: (v) => titleCase(v),
    },
    birthstone: {
      get: (p) => p.birthstone,
      values: (data) => MONTH_ORDER.filter((m) => data.some((p) => p.birthstone === m)),
      label: (v) => MONTH_LABEL[v] || titleCase(v),
    },
    color: {
      get: (p) => p.color,
      values: (data) => COLOR_ORDER.filter((c) => data.some((p) => p.color === c)),
      label: (v) => COLOR_LABEL[v] || titleCase(v),
      swatch: (v) => COLOR_SWATCH[v],
    },
    bestseller: {
      get: (p) => (p.bestseller ? 'yes' : null),
      values: (data) => (data.some((p) => p.bestseller) ? ['yes'] : []),
      label: () => 'Best Sellers',
    },
  };

  const PAGE_SIZE = 24;

  function boot(PRODUCTS_DATA) {
    if (loadingState) loadingState.classList.add('hidden');
    if (!PRODUCTS_DATA.length) {
      if (emptyState) emptyState.classList.remove('hidden');
      if (shownCount) shownCount.textContent = 'Showing 0 pieces';
      return;
    }

    const PRICE_MIN = Math.min(...PRODUCTS_DATA.map((p) => p.price));
    const PRICE_MAX = Math.max(...PRODUCTS_DATA.map((p) => p.price));

    const state = {
      search: '',
      facets: { vendor: new Set(), category: new Set(), birthstone: new Set(), color: new Set(), bestseller: new Set() },
      priceMin: PRICE_MIN,
      priceMax: PRICE_MAX,
      sort: 'featured',
      visibleCount: PAGE_SIZE,
    };

    function applyFilters(list, except) {
      let result = list;
      if (state.search) {
        const q = state.search.toLowerCase();
        result = result.filter((p) =>
          p.title.toLowerCase().includes(q) ||
          (p.vendor || '').toLowerCase().includes(q) ||
          (p.category || '').toLowerCase().includes(q) ||
          (p.birthstone && (MONTH_LABEL[p.birthstone] || '').toLowerCase().includes(q)) ||
          (p.color && (COLOR_LABEL[p.color] || '').toLowerCase().includes(q)));
      }
      Object.keys(FACETS).forEach((key) => {
        if (except === key) return;
        const set = state.facets[key];
        if (!set.size) return;
        const getter = FACETS[key].get;
        result = result.filter((p) => set.has(getter(p)));
      });
      if (except !== 'price' && (state.priceMin > PRICE_MIN || state.priceMax < PRICE_MAX)) {
        result = result.filter((p) => p.price >= state.priceMin && p.price <= state.priceMax);
      }
      return result;
    }

    function getFiltered() {
      const list = applyFilters(PRODUCTS_DATA, null);
      if (state.sort === 'price-asc') return [...list].sort((a, b) => a.price - b.price);
      if (state.sort === 'price-desc') return [...list].sort((a, b) => b.price - a.price);
      // "Featured" (default): jewellery first, non-jewellery pushed to the
      // back — a stable sort, so order within each group is untouched.
      return [...list].sort((a, b) => Number(isJewelry(b)) - Number(isJewelry(a)));
    }

    function hasActiveFilters() {
      return !!(state.search || Object.values(state.facets).some((s) => s.size) ||
        state.priceMin > PRICE_MIN || state.priceMax < PRICE_MAX);
    }

    function renderFacet(key) {
      const wrap = document.querySelector(`[data-facet-options="${key}"]`);
      if (!wrap) return;
      const config = FACETS[key];
      const scope = applyFilters(PRODUCTS_DATA, key);
      const counts = new Map();
      scope.forEach((p) => {
        const v = config.get(p);
        if (v == null) return;
        counts.set(v, (counts.get(v) || 0) + 1);
      });

      const activeSet = state.facets[key];
      const id = (v) => `facet-${key}-${v.toString().replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

      wrap.innerHTML = config.values(PRODUCTS_DATA).map((v) => {
        const count = counts.get(v) || 0;
        const checked = activeSet.has(v);
        const label = config.label ? config.label(v) : v;
        const swatch = config.swatch
          ? `<span class="facet-swatch" style="background:${config.swatch(v)}" aria-hidden="true"></span>`
          : '';
        return `
          <label class="facet-option${count === 0 && !checked ? ' is-zero' : ''}" for="${id(v)}">
            <span class="facet-option-label">
              <span class="facet-option-box" aria-hidden="true">
                <input type="checkbox" id="${id(v)}" data-facet-checkbox="${key}" value="${escapeHtml(v)}" ${checked ? 'checked' : ''} />
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg>
              </span>
              ${swatch}
              <span>${escapeHtml(label)}</span>
            </span>
            <span class="facet-option-count">(${count})</span>
          </label>`;
      }).join('');
    }

    /* ---------------- Price slider ---------------- */
    const priceMinInput = document.querySelector('[data-price-min-input]');
    const priceMaxInput = document.querySelector('[data-price-max-input]');
    const rangeMin = document.querySelector('[data-price-range-min]');
    const rangeMax = document.querySelector('[data-price-range-max]');
    const rangeFill = document.querySelector('[data-price-slider-range]');
    const priceMinLabel = document.querySelector('[data-price-min-label]');
    const priceMaxLabel = document.querySelector('[data-price-max-label]');

    function initPriceSlider() {
      [rangeMin, rangeMax].forEach((el) => { el.min = PRICE_MIN; el.max = PRICE_MAX; });
      rangeMin.value = PRICE_MIN;
      rangeMax.value = PRICE_MAX;
      priceMinInput.min = PRICE_MIN; priceMinInput.max = PRICE_MAX;
      priceMaxInput.min = PRICE_MIN; priceMaxInput.max = PRICE_MAX;
      priceMinInput.value = PRICE_MIN;
      priceMaxInput.value = PRICE_MAX;
      syncPriceUI();
    }

    function syncPriceUI() {
      const pct = (v) => ((v - PRICE_MIN) / (PRICE_MAX - PRICE_MIN || 1)) * 100;
      rangeFill.style.left = `${pct(state.priceMin)}%`;
      rangeFill.style.right = `${100 - pct(state.priceMax)}%`;
      priceMinLabel.textContent = fmtPrice(state.priceMin);
      priceMaxLabel.textContent = fmtPrice(state.priceMax);
    }

    function setPriceRange(min, max, opts) {
      min = Math.max(PRICE_MIN, Math.min(min, max));
      max = Math.min(PRICE_MAX, Math.max(max, min));
      state.priceMin = min;
      state.priceMax = max;
      rangeMin.value = min;
      rangeMax.value = max;
      priceMinInput.value = min;
      priceMaxInput.value = max;
      syncPriceUI();
      if (!(opts && opts.silent)) onFilterChange();
    }

    rangeMin.addEventListener('input', () => setPriceRange(Number(rangeMin.value), state.priceMax));
    rangeMax.addEventListener('input', () => setPriceRange(state.priceMin, Number(rangeMax.value)));
    priceMinInput.addEventListener('change', () => setPriceRange(Number(priceMinInput.value) || PRICE_MIN, state.priceMax));
    priceMaxInput.addEventListener('change', () => setPriceRange(state.priceMin, Number(priceMaxInput.value) || PRICE_MAX));

    /* ---------------- Facet collapse ---------------- */
    document.querySelectorAll('[data-facet-trigger]').forEach((btn) => {
      btn.addEventListener('click', () => {
        btn.closest('[data-facet-block]').classList.toggle('is-collapsed');
      });
    });

    /* ---------------- Mobile drawer ---------------- */
    const sidebar = document.querySelector('[data-filter-sidebar]');
    const scrim = document.querySelector('[data-filters-scrim]');
    function openDrawer() {
      sidebar.classList.add('is-open');
      scrim.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    }
    function closeDrawer() {
      sidebar.classList.remove('is-open');
      scrim.classList.add('hidden');
      document.body.style.overflow = '';
    }
    document.querySelector('[data-filters-open]')?.addEventListener('click', openDrawer);
    document.querySelector('[data-filters-close]')?.addEventListener('click', closeDrawer);
    scrim?.addEventListener('click', closeDrawer);

    /* ---------------- Cards + render ---------------- */
    function cardHTML(p) {
      const priceMarkup = p.onSale
        ? `<span class="line-through text-inksoft/50 mr-1.5">${p.compareAtDisplay}</span><span class="text-gold-dark font-medium">${p.priceDisplay}</span>`
        : p.priceDisplay;
      const saleBadge = p.onSale ? '<span class="sale-badge">Sale</span>' : '';
      const title = escapeHtml(p.title);
      const altImg = p.image2
        ? `<img src="${p.image2}" alt="" loading="lazy" aria-hidden="true" class="art-frame-img-alt" />`
        : '';
      const variantAttr = p.firstAvailableVariantId ? ` data-variant-id="${p.firstAvailableVariantId}"` : '';
      return `
        <a href="${p.url}" class="product-card block" data-category="${escapeHtml(p.category)}" data-price="${p.price}">
          <div class="art-frame aspect-square relative overflow-hidden">
            ${saleBadge}
            <img src="${p.image}" alt="${title}" loading="lazy" />
            ${altImg}
            <button class="wishlist-btn" data-wishlist aria-label="Add to wishlist"><svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#1C1917" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20s-7-4.4-9.5-9A5.5 5.5 0 0112 5a5.5 5.5 0 019.5 6c-2.5 4.6-9.5 9-9.5 9z"/></svg></button>
            <div class="quick-add"><button class="btn btn-primary w-full justify-center !py-3" data-add-cart${variantAttr}><span data-label>Quick Add</span></button></div>
          </div>
          <div class="mt-4">
            <h3 class="font-display text-lg leading-snug">${title}</h3>
            <p class="text-sm text-inksoft mt-1">${priceMarkup}</p>
          </div>
        </a>`;
    }

    function render(opts) {
      const append = !!(opts && opts.append);
      const filtered = getFiltered();
      const toShow = filtered.slice(0, state.visibleCount);
      const prevCount = append ? grid.children.length : 0;

      if (!append) grid.innerHTML = '';
      const newSlice = toShow.slice(prevCount);
      grid.insertAdjacentHTML('beforeend', newSlice.map(cardHTML).join(''));

      if (emptyState) emptyState.classList.toggle('hidden', filtered.length > 0);
      if (loadMoreWrap) loadMoreWrap.classList.toggle('hidden', state.visibleCount >= filtered.length || filtered.length === 0);
      if (shownCount) {
        shownCount.textContent = filtered.length
          ? `Showing ${toShow.length} of ${filtered.length} piece${filtered.length === 1 ? '' : 's'}`
          : 'Showing 0 pieces';
      }

      clearButtons.forEach((btn) => btn.classList.toggle('hidden', !hasActiveFilters()));

      const newCards = Array.from(grid.children).slice(prevCount);
      if (hasGsap && !prefersReducedMotion && newCards.length) {
        gsap.fromTo(newCards, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.4, stagger: 0.04, ease: 'power2.out' });
      } else {
        newCards.forEach((c) => { c.style.opacity = '1'; });
      }

      Object.keys(FACETS).forEach(renderFacet);
      wireFacetCheckboxes();
      renderActiveFilters();
    }

    /* ---------------- Active-filter chips ---------------- */
    const activeFiltersWrap = document.querySelector('[data-active-filters]');
    const removeIconSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

    function chipHTML(type, value, label, swatch) {
      const swatchHTML = swatch ? `<span class="active-filter-chip-swatch" style="background:${swatch}" aria-hidden="true"></span>` : '';
      return `<span class="active-filter-chip">${swatchHTML}${escapeHtml(label)}<button type="button" data-remove-filter="${type}" data-remove-value="${escapeHtml(value)}" aria-label="Remove ${escapeHtml(label)} filter">${removeIconSvg}</button></span>`;
    }

    function renderActiveFilters() {
      if (!activeFiltersWrap) return;
      const chips = [];
      if (state.search) chips.push(chipHTML('search', state.search, `Search: "${state.search}"`));
      Object.keys(FACETS).forEach((key) => {
        const config = FACETS[key];
        state.facets[key].forEach((v) => {
          const label = config.label ? config.label(v) : v;
          chips.push(chipHTML(key, v, label, config.swatch ? config.swatch(v) : null));
        });
      });
      if (state.priceMin > PRICE_MIN || state.priceMax < PRICE_MAX) {
        chips.push(chipHTML('price', 'price', `${fmtPrice(state.priceMin)} – ${fmtPrice(state.priceMax)}`));
      }
      activeFiltersWrap.innerHTML = chips.join('');
    }

    activeFiltersWrap?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove-filter]');
      if (!btn) return;
      const type = btn.dataset.removeFilter;
      const value = btn.dataset.removeValue;
      if (type === 'search') {
        state.search = '';
        if (searchInput) searchInput.value = '';
      } else if (type === 'price') {
        setPriceRange(PRICE_MIN, PRICE_MAX);
        return;
      } else if (state.facets[type]) {
        state.facets[type].delete(value);
      }
      onFilterChange();
    });

    function wireFacetCheckboxes() {
      document.querySelectorAll('[data-facet-checkbox]').forEach((cb) => {
        cb.addEventListener('change', () => {
          const key = cb.dataset.facetCheckbox;
          const set = state.facets[key];
          if (cb.checked) set.add(cb.value); else set.delete(cb.value);
          onFilterChange();
        });
      });
    }

    function onFilterChange() {
      state.visibleCount = PAGE_SIZE;
      render();
    }

    let searchDebounce;
    searchInput?.addEventListener('input', (e) => {
      clearTimeout(searchDebounce);
      const value = e.target.value;
      searchDebounce = setTimeout(() => {
        state.search = value.trim();
        onFilterChange();
      }, 150);
    });

    sortSelects.forEach((sel) => {
      sel.addEventListener('change', (e) => {
        state.sort = e.target.value;
        sortSelects.forEach((other) => { if (other !== e.target) other.value = state.sort; });
        onFilterChange();
      });
    });

    loadMoreBtn?.addEventListener('click', () => {
      state.visibleCount += PAGE_SIZE;
      render({ append: true });
    });

    clearButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        state.search = '';
        Object.values(state.facets).forEach((set) => set.clear());
        if (searchInput) searchInput.value = '';
        setPriceRange(PRICE_MIN, PRICE_MAX, { silent: true });
        onFilterChange();
      });
    });

    function applyUrlFilters() {
      const params = new URLSearchParams(window.location.search);
      const category = params.get('category');
      const birthstone = params.get('birthstone');
      const color = params.get('color');
      const bestseller = params.get('bestseller');
      if (category) state.facets.category.add(category);
      if (birthstone && MONTH_ORDER.includes(birthstone)) state.facets.birthstone.add(birthstone);
      if (color && COLOR_ORDER.includes(color)) state.facets.color.add(color);
      if (bestseller === 'true') state.facets.bestseller.add('yes');
    }

    applyUrlFilters();
    initPriceSlider();
    render();
  }

  loadAllProducts()
    .then((rawProducts) => boot(rawProducts.map(normalizeProduct)))
    .catch(() => {
      if (loadingState) loadingState.classList.add('hidden');
      if (emptyState) {
        emptyState.classList.remove('hidden');
        const msg = emptyState.querySelector('p:last-of-type');
        if (msg) msg.textContent = 'We could not load the collection right now — please refresh the page.';
      }
    });
})();
